import { createIcons, Share2 } from 'lucide';
import { trackShareOpen, trackSharePromptView } from '../lib/metrika';
import {
  notifyInstallActionsChanged,
  revealSaveSiteCta,
  saveSiteCtaHtml,
} from './save-site-cta';

const SITE_ORIGIN = 'https://пдфка.рф';
const CHANGE_EVENT = 'pdfka:post-download-actions-changed';

export function postDownloadCtaHtml(hidden = false): string {
  if (!supportsNativeShare()) return saveSiteCtaHtml(hidden);

  return `
    <button type="button" class="post-download-cta" data-post-download-cta data-share-site aria-label="Отправить ПДФку другу или коллеге" ${hidden ? 'hidden data-post-download-cta-deferred' : ''}>
      <span class="post-download-cta-icon" aria-hidden="true"><i data-lucide="share-2"></i></span>
      <span class="post-download-cta-copy">
        <span class="post-download-cta-title">ПДФка пригодилась?</span>
        <span class="post-download-cta-text">Отправьте её другу или коллеге. Заодно ссылка останется у вас в переписке.</span>
      </span>
      <span class="post-download-cta-action" aria-hidden="true">Отправить</span>
    </button>
  `;
}

export function revealPostDownloadCta(host: ParentNode): void {
  revealSaveSiteCta(host);
  host.querySelectorAll<HTMLElement>('[data-post-download-cta]').forEach((cta) => {
    cta.hidden = false;
    cta.removeAttribute('data-post-download-cta-deferred');
  });
  notifyPostDownloadActionsChanged();
}

export function notifyPostDownloadActionsChanged(): void {
  notifyInstallActionsChanged();
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function setupPostDownloadAction(root: HTMLElement): void {
  const sync = () => {
    createIcons({
      icons: { Share2 },
      root,
      attrs: {
        width: 20,
        height: 20,
        'stroke-width': 2,
      },
    });

    root.querySelectorAll<HTMLButtonElement>('[data-share-site]').forEach((button) => {
      if (button.hidden || button.hasAttribute('data-share-view-tracked')) return;
      button.setAttribute('data-share-view-tracked', '');
      trackSharePromptView();
    });
  };

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLButtonElement>('[data-share-site]');
    if (!button || !root.contains(button) || button.disabled) return;
    event.preventDefault();
    void openNativeShare(button);
  });

  window.addEventListener(CHANGE_EVENT, sync);
  sync();
}

async function openNativeShare(button: HTMLButtonElement): Promise<void> {
  if (!supportsNativeShare()) {
    replaceWithSaveAction(button);
    return;
  }

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  trackShareOpen('native');

  try {
    await navigator.share(shareData());
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== 'AbortError') {
      replaceWithSaveAction(button);
      return;
    }
  } finally {
    if (button.isConnected) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

function replaceWithSaveAction(button: HTMLButtonElement): void {
  button.outerHTML = saveSiteCtaHtml();
  notifyInstallActionsChanged();
}

function shareData(): ShareData {
  const toolName = currentToolName();
  return {
    title: `${toolName} — ПДФка`,
    text: `Удобный инструмент «${toolName}»: работает прямо в браузере, без загрузки файлов на сервер.`,
    url: shareUrl(),
  };
}

function currentToolName(): string {
  const heading = document.querySelector<HTMLHeadingElement>('main h1')?.textContent?.trim();
  if (!heading) return 'ПДФ-инструменты онлайн';
  return heading.replace(/\s+онлайн(?=\s|$|:)/i, '').trim();
}

function shareUrl(): string {
  const hashTool = location.hash.replace(/^#\/?/, '').split('/')[0];
  const pathname = location.pathname === '/' && hashTool && !['settings', 'privacy'].includes(hashTool)
    ? `/${hashTool}/`
    : location.pathname;
  return `${SITE_ORIGIN}${pathname}?from=share`;
}

function supportsNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}
